/* eslint-disable unicorn/filename-case -- pre-existing filename convention */
import { useState } from 'react';
import { Loading01 } from '@untitled-ui/icons-react';
import { useTranslation } from 'react-i18next';

import { getAuthPageChrome } from '@/components/auth/auth-page-chrome';
import { Button } from '@/components/base/buttons/button';

import { MFACodeInput } from './MFACodeInput';

interface MFALoginViewProps {
  /**
   * Empty when the sign-in method never collected one - the discoverable passkey
   * flow identifies the user from the credential, so there is no address to echo.
   */
  readonly userEmail: string;
  readonly onVerify: (code: string) => Promise<void>;
  readonly onBack: () => void;
  readonly isVerifying: boolean;
  readonly autoFocusCode?: boolean;
}

/**
 * MFA verification view for login page
 * Shown after successful password authentication when user has MFA enabled
 */
export function MFALoginView({
  userEmail,
  onVerify,
  onBack,
  isVerifying,
  autoFocusCode = true,
}: MFALoginViewProps) {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');
  // Resolved per render, not at module scope: the sheet and the web page mount
  // the same view, and only the render knows which surface it is on.
  const chrome = getAuthPageChrome();

  const handleVerify = async (value?: string) => {
    const codeToVerify = value ?? code;
    if (codeToVerify.length === 6) {
      await onVerify(codeToVerify);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-2 text-center">
        <h1 className={chrome.heading}>{t('loginView.heading')}</h1>
        <p className={`${chrome.description} text-balance`}>{t('loginView.description')}</p>
      </div>

      {userEmail === '' ? null : (
        <p className={`text-center ${chrome.mutedText}`}>
          {t('loginView.signingInAs')} <span className={chrome.mutedEmphasis}>{userEmail}</span>
        </p>
      )}

      <div className="flex justify-center">
        <MFACodeInput
          disabled={isVerifying}
          shouldAutoFocus={autoFocusCode}
          slotClassName={chrome.codeCell}
          value={code}
          onChange={setCode}
          onComplete={(value) => void handleVerify(value)}
        />
      </div>

      <Button
        className={chrome.primaryButton}
        isDisabled={code.length !== 6 || isVerifying}
        size="md"
        onClick={() => void handleVerify()}
      >
        {isVerifying ? (
          <>
            <Loading01 className="mr-2 size-4 animate-spin" />
            {t('loginView.verifying')}
          </>
        ) : (
          t('loginView.verify')
        )}
      </Button>

      <Button
        className={`w-full ${chrome.secondaryButton}`}
        color="tertiary"
        isDisabled={isVerifying}
        size="md"
        onClick={onBack}
      >
        {t('loginView.backToSignIn')}
      </Button>
    </div>
  );
}
